import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from './supabaseClient'
import './AdminPanel.css'

const ADMIN_EMAIL = 'christopherjbaldock@gmail.com'

const DIFF_FIELDS = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name',  label: 'Last Name'  },
  { key: 'title',      label: 'Title'      },
  { key: 'email',      label: 'Email'      },
  { key: 'phone',      label: 'Phone'      },
  { key: 'instagram',  label: 'Instagram'  },
  { key: 'twitter',    label: 'Twitter'    },
]

function fieldsDiffer(a, b, key) {
  return (a?.[key] || '') !== (b?.[key] || '')
}

export default function AdminPanel({ user }) {
  const navigate = useNavigate()

  useEffect(() => { document.title = 'Admin — CommitHQ' }, [])

  useEffect(() => {
    if (user.email !== ADMIN_EMAIL) navigate('/', { replace: true })
  }, [user.email, navigate])

  // ── New submissions ──────────────────────────────────────────────
  const [rows, setRows]         = useState([])
  const [loading, setLoading]   = useState(true)
  const [actingId, setActingId] = useState(null)

  const fetchPending = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('coaches_pending')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
    setRows(data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    if (user.email === ADMIN_EMAIL) fetchPending()
  }, [user.email, fetchPending])

  async function handleApprove(id) {
    setActingId(id)
    const row = rows.find(r => r.id === id)

    // If approving a Head Coach, delete the stale imported row for that school first
    if (row.title === 'Head Coach') {
      const { error: deleteError } = await supabase
        .from('coaches_db')
        .delete()
        .eq('school_name', row.school_name)
        .eq('title', 'Head Coach')
      if (deleteError) console.error('coaches_db delete (stale head coach) error:', deleteError)
    }

    const [updateRes, insertRes] = await Promise.all([
      supabase
        .from('coaches_pending')
        .update({ status: 'approved' })
        .eq('id', id),
      supabase
        .from('coaches_db')
        .insert({
          school_name:  row.school_name,
          division:     row.division,
          first_name:   row.first_name,
          last_name:    row.last_name,
          title:        row.title,
          email:        row.email,
          phone:        row.phone,
          instagram:    row.instagram,
          twitter:      row.twitter,
          approved_by:  user.id,
          approved_at:  new Date().toISOString(),
        }),
    ])
    if (updateRes.error) console.error('coaches_pending update error:', updateRes.error)
    if (insertRes.error) console.error('coaches_db insert error:', insertRes.error)
    if (!updateRes.error && !insertRes.error) {
      setRows(prev => prev.filter(r => r.id !== id))
    }
    setActingId(null)
  }

  async function handleReject(id) {
    setActingId(id)
    await supabase.from('coaches_pending').update({ status: 'rejected' }).eq('id', id)
    setRows(prev => prev.filter(r => r.id !== id))
    setActingId(null)
  }

  // ── Edit suggestions ─────────────────────────────────────────────
  const [editRows, setEditRows]         = useState([])
  const [currentCoaches, setCurrentCoaches] = useState({}) // coaches_db_id → coaches_db row
  const [editLoading, setEditLoading]   = useState(true)
  const [editActingId, setEditActingId] = useState(null)

  const fetchEditPending = useCallback(async () => {
    setEditLoading(true)
    const { data: edits } = await supabase
      .from('coaches_edits_pending')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false })

    const editsList = edits || []

    const ids = [...new Set(editsList.map(e => e.coaches_db_id).filter(Boolean))]
    let coachesMap = {}
    if (ids.length > 0) {
      const { data: coaches } = await supabase
        .from('coaches_db')
        .select('*')
        .in('id', ids)
      for (const c of (coaches || [])) {
        coachesMap[c.id] = c
      }
    }

    setEditRows(editsList)
    setCurrentCoaches(coachesMap)
    setEditLoading(false)
  }, [])

  useEffect(() => {
    if (user.email === ADMIN_EMAIL) fetchEditPending()
  }, [user.email, fetchEditPending])

  async function handleApproveEdit(id) {
    setEditActingId(id)
    const edit = editRows.find(r => r.id === id)
    const [pendingRes, coachRes] = await Promise.all([
      supabase
        .from('coaches_edits_pending')
        .update({ status: 'approved' })
        .eq('id', id),
      supabase
        .from('coaches_db')
        .update({
          first_name: edit.first_name,
          last_name:  edit.last_name,
          title:      edit.title,
          email:      edit.email,
          phone:      edit.phone,
          instagram:  edit.instagram,
          twitter:    edit.twitter,
        })
        .eq('id', edit.coaches_db_id),
    ])
    if (pendingRes.error) console.error('coaches_edits_pending update error:', pendingRes.error)
    if (coachRes.error)   console.error('coaches_db update error:', coachRes.error)
    if (!pendingRes.error && !coachRes.error) {
      setEditRows(prev => prev.filter(r => r.id !== id))
    }
    setEditActingId(null)
  }

  async function handleRejectEdit(id) {
    setEditActingId(id)
    await supabase.from('coaches_edits_pending').update({ status: 'rejected' }).eq('id', id)
    setEditRows(prev => prev.filter(r => r.id !== id))
    setEditActingId(null)
  }

  const handleSignOut = async () => { await supabase.auth.signOut() }

  if (user.email !== ADMIN_EMAIL) return null

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-header-left">
          <svg width="32" height="36" viewBox="0 0 64 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="64" height="72" rx="10" fill="#0D2340"/>
            <rect width="64" height="10" fill="#1D9E75"/>
            <rect width="64" height="5" y="5" fill="#1D9E75"/>
            <text x="32" y="50" textAnchor="middle" fontFamily="Georgia, serif" fontSize="34" fontWeight="bold" fill="white">C</text>
            <text x="32" y="64" textAnchor="middle" fontFamily="Georgia, serif" fontSize="11" fontWeight="bold" fill="#1D9E75">HQ</text>
          </svg>
          <div>
            <h1 className="admin-title">Admin Panel</h1>
            <p className="admin-subtitle">Pending coach submissions</p>
          </div>
        </div>
        <div className="admin-header-right">
          <span className="admin-user">{user.email}</span>
          <button className="admin-back" onClick={() => navigate('/')}>← App</button>
          <button className="admin-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </header>

      <div className="admin-body">

        {/* ── Section 1: New coach submissions ── */}
        <div className="admin-section">
          <h2 className="admin-section-heading">New Coach Submissions</h2>
          {loading ? (
            <p className="admin-loading">Loading submissions…</p>
          ) : rows.length === 0 ? (
            <div className="admin-empty">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <p>No pending submissions.</p>
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>School</th>
                    <th>Name</th>
                    <th>Title</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Instagram</th>
                    <th>Twitter</th>
                    <th>Submitted by</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const isEdit = row.submission_type === 'edit'
                    return (
                      <tr key={row.id} className={isEdit ? 'admin-row-edit' : ''}>
                        <td className="admin-cell-type">
                          {isEdit ? (
                            <div className="admin-type-edit-wrap">
                              <span className="admin-badge-edit">EDIT SUGGESTION</span>
                              {row.original_source && (
                                <span className="admin-edit-source">{row.original_source === 'coaches_db' ? 'verified DB' : 'schools DB'}</span>
                              )}
                            </div>
                          ) : (
                            <span className="admin-badge-new">NEW</span>
                          )}
                          {isEdit && row.edit_notes && (
                            <div className="admin-edit-notes">
                              <span className="admin-edit-notes-label">Notes:</span>
                              <span className="admin-edit-notes-text">{row.edit_notes}</span>
                            </div>
                          )}
                        </td>
                        <td className="admin-cell-school">{row.school_name}</td>
                        <td className="admin-cell-name">{row.first_name} {row.last_name}</td>
                        <td>{row.title || <span className="admin-empty-cell">—</span>}</td>
                        <td>
                          {row.email
                            ? <a href={`mailto:${row.email}`} className="admin-link">{row.email}</a>
                            : <span className="admin-empty-cell">—</span>}
                        </td>
                        <td>{row.phone || <span className="admin-empty-cell">—</span>}</td>
                        <td>
                          {row.instagram
                            ? <a href={`https://instagram.com/${row.instagram.replace(/^@/, '')}`} className="admin-link" target="_blank" rel="noreferrer">@{row.instagram.replace(/^@/, '')}</a>
                            : <span className="admin-empty-cell">—</span>}
                        </td>
                        <td>
                          {row.twitter
                            ? <a href={`https://x.com/${row.twitter.replace(/^@/, '')}`} className="admin-link" target="_blank" rel="noreferrer">@{row.twitter.replace(/^@/, '')}</a>
                            : <span className="admin-empty-cell">—</span>}
                        </td>
                        <td className="admin-cell-submitter">{row.submitted_by}</td>
                        <td>
                          <div className="admin-actions">
                            <button
                              className="btn-approve"
                              onClick={() => handleApprove(row.id)}
                              disabled={actingId === row.id}
                            >
                              Approve
                            </button>
                            <button
                              className="btn-reject"
                              onClick={() => handleReject(row.id)}
                              disabled={actingId === row.id}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Section 2: Coach edit suggestions ── */}
        <div className="admin-section">
          <h2 className="admin-section-heading">Coach Edit Suggestions</h2>
          {editLoading ? (
            <p className="admin-loading">Loading edit suggestions…</p>
          ) : editRows.length === 0 ? (
            <div className="admin-empty">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <p>No pending edit suggestions.</p>
            </div>
          ) : (
            <div className="edit-suggestion-list">
              {editRows.map(edit => {
                const current = currentCoaches[edit.coaches_db_id] || null
                return (
                  <div key={edit.id} className="edit-suggestion-card">
                    <div className="edit-suggestion-meta">
                      <div className="edit-suggestion-meta-left">
                        <span className="edit-suggestion-school">{edit.school_name}</span>
                        {current && (
                          <span className="edit-suggestion-coach-name">
                            {current.first_name} {current.last_name}
                          </span>
                        )}
                      </div>
                      <span className="edit-suggestion-submitter">{edit.submitted_by}</span>
                    </div>

                    <div className="edit-diff-table-wrap">
                      <table className="edit-diff-table">
                        <thead>
                          <tr>
                            <th className="edit-diff-th-field">Field</th>
                            <th className="edit-diff-th-current">Current Data</th>
                            <th className="edit-diff-th-suggested">Suggested Edit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {DIFF_FIELDS.map(({ key, label }) => {
                            const differs = current ? fieldsDiffer(current, edit, key) : false
                            const currentVal  = current?.[key] || ''
                            const suggestedVal = edit[key] || ''
                            return (
                              <tr key={key} className={differs ? 'edit-diff-row-changed' : ''}>
                                <td className="edit-diff-cell-field">{label}</td>
                                <td className={`edit-diff-cell-current${differs ? ' edit-diff-changed' : ''}`}>
                                  {currentVal || <span className="admin-empty-cell">—</span>}
                                </td>
                                <td className={`edit-diff-cell-suggested${differs ? ' edit-diff-changed' : ''}`}>
                                  {suggestedVal || <span className="admin-empty-cell">—</span>}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="edit-suggestion-actions">
                      <button
                        className="btn-reject"
                        onClick={() => handleRejectEdit(edit.id)}
                        disabled={editActingId === edit.id}
                      >
                        Reject
                      </button>
                      <button
                        className="btn-approve"
                        onClick={() => handleApproveEdit(edit.id)}
                        disabled={editActingId === edit.id}
                      >
                        Approve & Update
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}
